import PropTypes from 'prop-types';
import {
  Typography,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  TablePagination
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import Scrollbar from '../../../Scrollbar';
import ReportListHead from './ReportListHead';
import { fDate } from '../../../../utils/formatTime';
import ReportMoreMenu from './ReportMoreMenu';

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator) {
  if (array.size === 0) return array;
  array.sort((a, b) => {
    const order = comparator(a, b);
    if (order !== 0) return order;
    return a - b;
  });

  return array;
}

ReportTable.propTypes = {
  reports: PropTypes.array,
  editReport: PropTypes.func
};

export default function ReportTable({ reports, editReport }) {
  const { t } = useTranslation();

  const [orderBy, setOrderBy] = useState('title');
  const [order, setOrder] = useState('asc');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);

  const TABLE_HEAD = [
    { id: 'year', label: t('report.year'), alignRight: false },
    { id: 'unitsTransported', label: t('report.units_transported'), alignRight: false },
    { id: 'tonKilometerPerformance', label: t('report.ton_km_performance'), alignRight: false },
    { id: 'added', label: t('report.created'), alignRight: false },
    {}
  ];

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - reports.length) : 0;
  const isreportsEmpty = reports.length === 0;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredreports = applySortFilter(reports, getComparator(order, orderBy));

  return (
    <>
      <Scrollbar>
        <TableContainer sx={{ minWidth: 800 }}>
          <Table>
            <ReportListHead
              order={order}
              orderBy={orderBy}
              headLabel={TABLE_HEAD}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {filteredreports
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  const { id, year, unitsTransported, added, tonKilometerPerformance } = row;
                  return (
                    <TableRow
                      hover
                      key={`${id}${index}`}
                      tabIndex={-1}
                      onClick={() => editReport(id)}
                    >
                      <TableCell component="th" scope="row">
                        <Typography variant="subtitle2" noWrap>
                          {year}
                        </Typography>
                      </TableCell>
                      <TableCell align="left">{unitsTransported}</TableCell>
                      <TableCell align="left">{tonKilometerPerformance}</TableCell>
                      <TableCell align="left">{fDate(added)}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <ReportMoreMenu id={id} edit={editReport} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              {emptyRows > 0 && (
                <TableRow key="Empty" style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
            {isreportsEmpty && (
              <TableBody key="no-report">
                <TableRow>
                  <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                    <Paper>
                      <Typography gutterBottom align="center" variant="subtitle1">
                        {t('report.none')}
                      </Typography>
                    </Paper>
                  </TableCell>
                </TableRow>
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </Scrollbar>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={reports.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </>
  );
}
